import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";

const abs = (u) => !u ? "" : u.startsWith("http") ? u : `${BACKEND_URL}${u}`;

/**
 * SellerProductEdit — seller-facing edit modal for their own products.
 * On save, the backend automatically resets status → pending (admin re-approval)
 * whenever material fields are changed on a previously-approved listing.
 */
export default function SellerProductEdit({ productId, onClose, onSaved }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!productId) return;
    api.get(`/products/${productId}`)
      .then(({ data }) => setP({
        ...data,
        price: data.price ?? "",
        stock: data.stock ?? "",
        delivery_days: data.delivery_days ?? "",
        shipping_price: data.shipping_price ?? "",
      }))
      .catch(() => { toast.error("Could not load product"); onClose && onClose(); });
  }, [productId, onClose]);

  const set = (k, v) => setP((cur) => ({ ...cur, [k]: v }));

  const uploadImage = async (file, position) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/uploads/product-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    if (position === "cover") set("image_url", data.url);
    else set("images", [...(p.images || []), data.url]);
    toast.success("Image uploaded");
  };

  const removeGalleryImage = (i) => set("images", (p.images || []).filter((_, j) => j !== i));

  const save = async () => {
    if (!p.title.trim()) return toast.error("Title required");
    const priceNum = parseFloat(p.price);
    if (isNaN(priceNum) || priceNum < 0) return toast.error("Price cannot be negative (use 0 for free)");
    setBusy(true);
    try {
      const payload = {
        title: p.title,
        description: p.description || "",
        price: priceNum,
        image_url: p.image_url || null,
        images: p.images || [],
      };
      if (p.type === "physical") {
        if (p.stock !== "" && p.stock != null) payload.stock = parseInt(p.stock, 10);
        if (p.delivery_days !== "" && p.delivery_days != null) payload.delivery_days = parseInt(p.delivery_days, 10);
        payload.free_shipping = !!p.free_shipping;
        payload.allow_pickup = !!p.allow_pickup;
        payload.shipping_vat_included = !!p.shipping_vat_included;
        if (p.shipping_price !== "" && p.shipping_price != null) payload.shipping_price = parseFloat(p.shipping_price);
      }
      const { data } = await api.patch(`/products/${productId}`, payload);
      if (data.status === "pending" && data.previous_status === "approved") {
        toast.success("Saved. Your edits are back in admin review — buyers keep seeing the previous approved version until we re-approve it.");
      } else {
        toast.success("Product updated");
      }
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!p) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose}>
        <div className="brut-border bg-white px-6 py-4 mono uppercase text-xs tracking-[0.2em]">Loading…</div>
      </div>
    );
  }

  const isStream = p.category === "stream";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="seller-product-edit-modal">
      <div className="brut-border bg-[#FDF3E9] w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b-2 border-[#1F3D2A] bg-white flex items-start justify-between gap-3">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Edit your listing</div>
            <h3 className="heading text-xl font-bold uppercase mt-1 line-clamp-1">{p.title}</h3>
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#555] mt-1">{p.category} · {p.type} · currently {p.status}</div>
          </div>
          <button onClick={onClose} data-testid="seller-edit-close" className="brut-border bg-white px-3 py-2 mono text-[10px] uppercase tracking-[0.25em] hover:bg-[#C77E3C] hover:text-white">✕ Close</button>
        </div>

        {p.status === "approved" && (
          <div className="mx-5 mt-5 brut-border bg-[#EDE5D2] p-3 mono text-[10px] uppercase tracking-[0.25em] text-[#1F3D2A]" data-testid="reapproval-notice">
            ⚠ Editing an approved listing sends it back to admin for re-approval. Buyers will keep seeing the current version until an admin approves your changes.
          </div>
        )}
        {isStream && (
          <div className="mx-5 mt-3 brut-border bg-white p-3 mono text-[10px] uppercase tracking-[0.25em] text-[#555]">
            To change chapters, re-submit as a new stream (e.g. &ldquo;Part 2&rdquo;). Title, description and price stay editable here.
          </div>
        )}

        <div className="p-5 space-y-4">
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] font-bold">Title *</label>
            <input data-testid="seller-edit-title" value={p.title} onChange={(e) => set("title", e.target.value)} className="w-full mt-1 brut-border bg-white px-3 py-2" />
          </div>

          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] font-bold">Description</label>
            <textarea data-testid="seller-edit-description" rows={4} value={p.description || ""} onChange={(e) => set("description", e.target.value)} className="w-full mt-1 brut-border bg-white px-3 py-2" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="mono text-[10px] uppercase tracking-[0.25em] font-bold">Price ($) — 0 = FREE</label>
              <input data-testid="seller-edit-price" type="number" min="0" step="0.01" value={p.price} onChange={(e) => set("price", e.target.value)} className="w-full mt-1 brut-border bg-white px-3 py-2" />
              {parseFloat(p.price) === 0 && <div className="mono text-[9px] uppercase tracking-[0.25em] text-[#C77E3C] mt-1">Free · buyer will not be charged</div>}
            </div>
            {p.type === "physical" && (
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.25em] font-bold">Stock</label>
                <input data-testid="seller-edit-stock" type="number" min="0" value={p.stock} onChange={(e) => set("stock", e.target.value)} className="w-full mt-1 brut-border bg-white px-3 py-2" />
              </div>
            )}
            {p.type === "physical" && (
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.25em] font-bold">Delivery days</label>
                <input data-testid="seller-edit-delivery" type="number" min="1" max="60" value={p.delivery_days} onChange={(e) => set("delivery_days", e.target.value)} className="w-full mt-1 brut-border bg-white px-3 py-2" />
              </div>
            )}
          </div>

          {p.type === "physical" && (
            <div className="brut-border bg-white p-3 space-y-3">
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">/ Shipping</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <label className="mono text-[10px] uppercase tracking-[0.25em] font-bold">Shipping $ (if paid)</label>
                  <input data-testid="seller-edit-shipping-price" type="number" min="0" step="0.01" value={p.shipping_price} onChange={(e) => set("shipping_price", e.target.value)} className="w-full mt-1 brut-border bg-white px-3 py-2" />
                </div>
                <label className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.25em] pt-6">
                  <input type="checkbox" data-testid="seller-edit-free-shipping" checked={!!p.free_shipping} onChange={(e) => set("free_shipping", e.target.checked)} /> Free shipping
                </label>
                <label className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.25em] pt-6">
                  <input type="checkbox" data-testid="seller-edit-allow-pickup" checked={!!p.allow_pickup} onChange={(e) => set("allow_pickup", e.target.checked)} /> Local pickup
                </label>
              </div>
              <label className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.25em]">
                <input type="checkbox" data-testid="seller-edit-vat-included" checked={!!p.shipping_vat_included} onChange={(e) => set("shipping_vat_included", e.target.checked)} /> Shipping VAT / tax included
              </label>
            </div>
          )}

          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] font-bold">Cover image</label>
            <div className="mt-1 flex items-center gap-3 flex-wrap">
              {p.image_url && <img src={abs(p.image_url)} alt="cover" className="w-24 h-24 brut-border object-cover" />}
              <input data-testid="seller-edit-cover-file" type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0], "cover")} className="brut-border bg-white px-3 py-2 text-sm" />
              {p.image_url && <button onClick={() => set("image_url", "")} className="mono text-[10px] uppercase tracking-[0.25em] text-[#C77E3C] hover:underline">clear</button>}
            </div>
          </div>

          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] font-bold">Gallery</label>
            <div className="mt-1">
              <input data-testid="seller-edit-gallery-file" type="file" accept="image/*" multiple onChange={async (e) => {
                const files = Array.from(e.target.files || []); e.target.value = "";
                for (const f of files) await uploadImage(f, "gallery");
              }} className="brut-border bg-white px-3 py-2 text-sm" />
              {p.images && p.images.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {p.images.map((u, i) => (
                    <div key={i} className="relative w-20 h-20 brut-border overflow-hidden group">
                      <img src={abs(u)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => removeGalleryImage(i)} className="absolute bottom-0 right-0 bg-[#1F3D2A] text-white mono text-[10px] px-1 opacity-0 group-hover:opacity-100">✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 border-t-2 border-[#1F3D2A] bg-white flex items-center gap-3">
          <button data-testid="seller-edit-save" onClick={save} disabled={busy} className="brut-border bg-[#C77E3C] text-white px-5 py-3 mono text-xs uppercase tracking-[0.25em] hover:bg-[#1F3D2A] disabled:opacity-40">
            {busy ? "Saving…" : p.status === "approved" ? "Save & resubmit for review" : "Save changes"}
          </button>
          <button onClick={onClose} className="brut-border bg-white px-5 py-3 mono text-xs uppercase tracking-[0.25em] hover:bg-[#FDF3E9]">Cancel</button>
        </div>
      </div>
    </div>
  );
}
